import { useCallback, useEffect } from 'react';
import requestDeduplicationService from 'services/requestDeduplicationService';
import rateLimiterService from 'services/rateLimiterService';

/**
 * Hook para requisições com deduplicação
 * Evita requests duplicadas durante navegação rápida
 * Reutiliza a mesma Promise para requisições idênticas
 */
export const useDeduplicatedRequest = () => {
  /**
   * Executa requisição com deduplicação
   * @param {Function} requestFn - Função que executa a requisição
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @param {number} timeout - Timeout em ms
   * @returns {Promise} - Promise da requisição
   */
  const executeRequest = useCallback(async (requestFn, url, params = {}, method = 'GET', timeout = 30000) => {
    try {      
      // Detectar se é requisição paralela (múltiplas empresas)
      const isParallelRequest = url.includes('empresas-resumo-') && url.includes('-2025-');
      
      let result;
      if (isParallelRequest) {
        // Para requisições paralelas, bypass rate limiter
        result = await requestFn(params);
      } else {
        // Para requisições sequenciais, usar rate limiter
        result = await rateLimiterService.executeRequest(
          requestFn,
          url,
          params,
          method,
          timeout
        );
      }
      
      return result;
    } catch (error) {
      console.error(`[executeRequest] Erro para ${url}:`, error);
      throw error;
    }
  }, []);

  /**
   * Verifica se existe requisição em voo
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @returns {boolean} - True se existe requisição em voo
   */
  const hasPendingRequest = useCallback((url, params = {}, method = 'GET') => {
    return requestDeduplicationService.hasPendingRequest(url, params, method);
  }, []);

  /**
   * Cancela requisição em voo
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @returns {boolean} - True se cancelou com sucesso
   */
  const cancelRequest = useCallback((url, params = {}, method = 'GET') => {
    return requestDeduplicationService.cancelRequest(url, params, method);
  }, []);

  /**
   * Limpa todas as requisições em voo
   */
  const clearAllRequests = useCallback(() => {
    requestDeduplicationService.clearAllRequests();
  }, []);

  /**
   * Obtém estatísticas do cache
   * @returns {Object} - Estatísticas do cache
   */
  const getStats = useCallback(() => {
    return requestDeduplicationService.getStats();
  }, []);

  // Limpar requisições obsoletas ao desmontar
  useEffect(() => {
    return () => {
      clearAllRequests();
    };
  }, [clearAllRequests]);

  return {
    executeRequest,
    hasPendingRequest,
    cancelRequest,
    clearAllRequests,
    getStats
  };
};

export default useDeduplicatedRequest;
